import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import {
  AgentJobStatus,
  isJobFinished,
} from '../../services/print-agent.service';
import {
  ITrackedJob,
  PrintFlowService,
} from '../../services/print-flow.service';
import { PrintAgentGuideComponent } from '../print-agent-guide/print-agent-guide';

interface IJobView {
  icon: string;
  tone: 'green' | 'blue' | 'gray' | 'red' | 'yellow';
  label: string;
}

/** Ilovada bir marta joylanadi: yo'riqnoma, tasdiqlash oynalari va chop etish navbati vidjeti. */
@Component({
  selector: 'lib-print-center',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    TooltipModule,
    PrintAgentGuideComponent,
  ],
  templateUrl: './print-center.html',
  styleUrl: './print-center.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrintCenterComponent {
  readonly flow = inject(PrintFlowService);

  readonly finishedCount = computed(
    () => this.flow.jobs().filter((j) => isJobFinished(j.status)).length,
  );

  isActive(job: ITrackedJob): boolean {
    return !isJobFinished(job.status);
  }

  canRetry(job: ITrackedJob): boolean {
    return job.status === 'error' || job.status === 'unknown';
  }

  jobView(job: ITrackedJob): IJobView {
    const t = (
      key: string,
      fallback: string,
      params?: Record<string, unknown>,
    ) => this.flow.t(key, fallback, params);

    const views: Record<AgentJobStatus, IJobView> = {
      queued: {
        icon: 'pi pi-clock',
        tone: 'yellow',
        label:
          job.position && job.position > 0
            ? t('print_job_queued_pos', 'Navbatda ({{pos}})', {
                pos: job.position,
              })
            : t('print_job_queued', 'Navbatda'),
      },
      spooling: {
        icon: 'pi pi-spin pi-spinner',
        tone: 'blue',
        label: t('print_job_spooling', 'Tayyorlanmoqda'),
      },
      printing: {
        icon: 'pi pi-spin pi-spinner',
        tone: 'blue',
        label:
          job.totalPages && job.pagesPrinted !== null
            ? t(
                'print_job_printing_pages',
                'Chop etilmoqda ({{done}}/{{total}} bet)',
                {
                  done: job.pagesPrinted,
                  total: job.totalPages,
                },
              )
            : t('print_job_printing', 'Chop etilmoqda'),
      },
      done: {
        icon: 'pi pi-check-circle',
        tone: 'green',
        label: t('print_job_done', 'Printerga yuborildi'),
      },
      error: {
        icon: 'pi pi-times-circle',
        tone: 'red',
        label: job.error || t('print_job_error', 'Xato'),
      },
      cancelled: {
        icon: 'pi pi-ban',
        tone: 'gray',
        label: t('print_job_cancelled', 'Bekor qilindi'),
      },
      unknown: {
        icon: 'pi pi-question-circle',
        tone: 'gray',
        label: t('print_job_unknown', "Holatini bilib bo'lmadi"),
      },
    };
    return views[job.status];
  }
}
